import { Injectable } from 'framework/di';

import { DomHelper } from '../../helpers/dom.helper';
import { handleRecentSearchForSuggestionTerm } from '../../instant-search-widget/recent-search';
import { AppService } from '../app/app.service';
import {
  FILTER_MOBILE_ICON_SELECTOR,
  FILTER_TREE_TOGGLE_BUTTON_SELECTOR,
  LIMIT,
} from './filter.constants';
import { FilterHelper } from './filter.helper';
import { EVENT_NAMES } from './filter.model';
import { FilterService } from './filter.service';
import { FilterStickyController } from './filter.sticky.controller';
import { FilterStore } from './filter.store';

// Pagination constants
const DEFAULT = 'default';
const LOAD_MORE = 'load_more';
const INFINITE = 'infinite_scroll';
const PREV_PAGE = 'prevPage';
const SELECTED_PRODUCT_PAGE = 'selectedProductPage';
const SELECTED_PRODUCT_ID = 'selectedProductId';
const COLLECTION_SELECTED = 'collectionSelected';

/**
 * Handler service for filter widget user interactions and UI state management.
 *
 * This service orchestrates all user interaction handlers for the filter system including
 * product clicks, sorting, pagination, view switching, and filter tree interactions.
 * It acts as the central event router that processes DOM events and translates them into
 * filter state changes and custom events. Can be extended by Technical Support teams to
 * customize interaction behaviors for shop-specific requirements.
 *
 * @remarks
 * Key responsibilities:
 * - Product item click handling and redirect logic
 * - Sort dropdown interaction and state management
 * - Pagination controls (default, load more, infinite scroll)
 * - View-as controls (grid/list view switching)
 * - Filter tree toggle and mobile interactions
 * - Limit list (products per page) controls
 * - Legacy metadata action routing
 * - Event emission for filter state changes
 *
 * All public methods emit events through EVENT_NAMES which can be intercepted
 * by extended implementations for additional custom logic.
 *
 * @example
 * Extend to add custom product click analytics:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
 *   return class CustomFilterHandler extends FilterHandler {
 *     handleClickProductItem(target) {
 *       // Track product clicks for analytics
 *       const productItem = target.closest('.boost-sd__product-item');
 *       if (productItem) {
 *         const productId = productItem.getAttribute('data-product-id');
 *         this.sendAnalytics('product_click', { productId });
 *       }
 *       return super.handleClickProductItem(target);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterHandler {
  constructor(
    private appService: AppService,
    private filterHelper: FilterHelper,
    private filterStore: FilterStore,
    private filterStickyController: FilterStickyController,
    private domHelper: DomHelper,
    private filterService: FilterService
  ) {}

  /**
   * Handles click events on product items in the product list.
   *
   * This method processes product item clicks, handling special cases like banner links,
   * embedded bundles, volume bundles, and wishlist buttons. For standard product clicks,
   * it constructs the product detail URL with variant information and navigates to it,
   * while also tracking the selected product page for analytics. Override this method
   * to customize product click behavior or add shop-specific tracking.
   *
   * @param target - The clicked DOM element
   *
   * @remarks
   * Special handling includes:
   * - Banner links: Opens URL directly (respects target="_blank")
   * - Embedded/volume bundles: Prevents default navigation
   * - Wishlist buttons: Prevents redirect to allow wishlist interaction
   * - Elements with data-no-redirect="true": Prevents navigation
   *
   * @example
   * Basic usage (called internally by handleCoreClick):
   * ```typescript
   * this.handleClickProductItem(event.target);
   * ```
   *
   * @example
   * Extend to add custom product click tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleClickProductItem(target) {
   *       const productItem = target.closest('.boost-sd__product-item');
   *       if (productItem) {
   *         // Send custom analytics before navigation
   *         const productId = productItem.getAttribute('data-product-id');
   *         gtag('event', 'product_click', { product_id: productId });
   *       }
   *       return super.handleClickProductItem(target);
   *     }
   *   };
   * });
   * ```
   */
  handleClickProductItem(target) {
    // check if click banner-link then open banner link
    if (target?.closest('.boost-sd__product-banner-link')) {
      const bannerElement = target?.closest('.boost-sd__product-banner-link');
      const url = bannerElement.getAttribute('href');
      const openNewTab = bannerElement.getAttribute('target') === '_blank';

      if (url) {
        if (openNewTab) {
          const newWindow = window.open(url, '_blank');
          newWindow?.focus();
        } else {
          window.location.href = url;
        }
      }

      return;
    }
    //check click on embedded bundle product list
    if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
      return;
    }

    //check click on volume bundle product list
    if (target?.closest('.boost-sd__product-item--volume-bundle')) {
      return;
    }

    //Check if need to redirect or not, exclude if the tagName is wishlist-button-collection
    const tagName = target?.tagName?.toLowerCase();
    const closestTagWishlist = target?.closest('wishlist-button-collection');
    const noRedirect = target?.dataset?.noRedirect;
    const closestNoRedirect = target?.closest("[data-no-redirect='true']");
    if (
      tagName === 'wishlist-button-collection' ||
      closestTagWishlist ||
      noRedirect === 'true' ||
      closestNoRedirect
    )
      return;

    const productItem = target.closest('.boost-sd__product-item');
    const { addCollectionToProductUrl, current_tags = [] }: any =
      this.appService.TAEAppConfig.generalSettings || {};

    if (productItem) {
      const { handle, splitProduct, variantId, variants }: any =
        this.filterHelper.safeParseJSON(productItem.getAttribute('data-product')) || {};
      const productId = productItem.getAttribute('data-product-id');

      const productUrl = this.filterHelper.buildProductDetailUrlWithVariant(
        { variants, variant_id: variantId, handle, split_product: splitProduct },
        addCollectionToProductUrl,
        current_tags
      );

      const index = this.getIndexOfProductItem(productItem);

      this.trackSelectedProductPage(index, productId);
      window.location.href = productUrl;
    }
  }

  /**
   * Gets the index of a product item within the product list.
   *
   * Searches through all product items in the boost-sd__product-list container
   * to find the index position of the specified target item. This is used for
   * pagination calculations and analytics tracking. Override this method to
   * customize how product indices are determined (e.g., filtering out specific items).
   *
   * @param targetItem - The target product item element to find
   * @returns The zero-based index of the product item, or -1 if not found
   *
   * @example
   * Extend to exclude certain products from index calculation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     protected getIndexOfProductItem(targetItem) {
   *       const document = this.filterStore.getState('document');
   *       // Exclude banner items from index calculation
   *       const productItems = Array.from(
   *         document.querySelectorAll('.boost-sd__product-list .boost-sd__product-item')
   *       ).filter(item => !item.classList.contains('boost-sd__product-item--banner'));
   *
   *       return productItems.indexOf(targetItem);
   *     }
   *   };
   * });
   * ```
   */
  protected getIndexOfProductItem(targetItem: Element): number {
    const document = this.filterStore.getState('document') as HTMLElement;
    const productItems = document.querySelectorAll(
      '.boost-sd__product-list .boost-sd__product-item'
    );

    for (let i = 0; i < productItems.length; i++) {
      if (productItems[i] === targetItem) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Tracks the selected product page for analytics and session storage.
   *
   * Calculates and stores the actual page number where a product was selected,
   * accounting for different pagination types (default, load_more, infinite_scroll).
   * This information is used for maintaining user context when returning from product
   * detail pages. Override this method to customize page tracking logic or add
   * additional analytics tracking.
   *
   * @param index - The zero-based index of the product in the current product list
   * @param productId - The unique product ID string
   *
   * @remarks
   * Pagination type calculations:
   * - default: Uses current page from URL
   * - load_more: Accumulates pages from previous page + calculated page
   * - infinite_scroll: Same as load_more
   *
   * Stores in session storage:
   * - SELECTED_PRODUCT_PAGE: The calculated page number
   * - SELECTED_PRODUCT_ID: The product ID
   * - COLLECTION_SELECTED: The current collection handle
   *
   * @example
   * Extend to add custom analytics tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     trackSelectedProductPage(index, productId) {
   *       super.trackSelectedProductPage(index, productId);
   *
   *       // Send to custom analytics service
   *       analytics.track('product_selected', {
   *         product_id: productId,
   *         position: index + 1,
   *         collection: this.filterService.getCurrentCollection()
   *       });
   *     }
   *   };
   * });
   * ```
   */
  trackSelectedProductPage(index: number, productId: string): void {
    const pageParam = this.filterHelper.getQueryParamByKey('page');
    const currPage = pageParam ? Number(pageParam) : 1;
    const defaultParams = this.filterStore.getState('defaultParams') as { limit?: number };
    const limit = defaultParams?.limit || 24;
    const page = Math.ceil((index + 1) / limit) || 1;
    const prevPage = this.filterHelper.getSessionStorage(PREV_PAGE, 1);
    const paginationType =
      this.appService.themeSettings?.additionalElements?.pagination?.paginationType || DEFAULT;

    const calc: Record<string, number> = {
      [DEFAULT]: currPage,
      [LOAD_MORE]: prevPage - 1 + page,
      [INFINITE]: prevPage - 1 + page,
    };

    if (!paginationType) return;

    const actualPage = calc[paginationType];
    const currentCollection = this.filterService.getCurrentCollection();

    this.filterHelper.setSessionStorage(SELECTED_PRODUCT_PAGE, actualPage);
    this.filterHelper.setSessionStorage(SELECTED_PRODUCT_ID, productId);
    this.filterHelper.setSessionStorage(COLLECTION_SELECTED, currentCollection);

    const { isShortenUrlParam } = this.appService.filterSettings;

    this.filterHelper.setQueryParamHistory('page', actualPage.toString(), true, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });
  }

  /**
   * Handles sort dropdown interaction and state changes.
   *
   * Manages the sort dropdown UI state and processes sort option selection.
   * Handles opening/closing the dropdown, selecting sort options, and triggering
   * filter resets when sort changes. Override this method to customize sort behavior
   * or add validation logic for specific sort options.
   *
   * @param target - The clicked element (sort button, value, or option)
   * @param originalEvent - Optional original event for stopping propagation when selecting options
   *
   * @remarks
   * Handles three interaction types:
   * - Click on option: Selects sort value and triggers filter reset
   * - Click on button/value: Toggles dropdown open/closed state
   * - Other clicks: Ignored
   *
   * Emits SORT_CHANGED event to update UI after state changes.
   *
   * @example
   * Extend to add sort option validation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleSort(target, originalEvent) {
   *       const isOption = target.classList.contains('boost-sd__sorting-option');
   *
   *       if (isOption) {
   *         const sort = target.getAttribute('data-value');
   *         // Restrict certain sort options for specific collections
   *         if (sort === 'price-ascending' && this.isGiftCardCollection()) {
   *           alert('Price sorting not available for gift cards');
   *           return;
   *         }
   *       }
   *
   *       return super.handleSort(target, originalEvent);
   *     }
   *   };
   * });
   * ```
   */
  handleSort(target: HTMLElement, originalEvent?: Event): void {
    const sortState = (this.filterStore.getState('sort') as {
      isShow: boolean;
      sort: string | null;
    }) || {
      isShow: false,
      sort: null,
    };

    const isValue = target.classList.contains('boost-sd__sorting-value');
    const isButton = target.classList.contains('boost-sd__sorting-button');
    const isOption = target.classList.contains('boost-sd__sorting-option');

    if (isOption) {
      if (originalEvent) originalEvent.stopPropagation();

      const sort = target.getAttribute('data-value');
      const { isShortenUrlParam } = this.appService.filterSettings;

      if (sortState.sort !== sort || sortState.isShow !== false) {
        this.filterStore.setState({
          sort: { sort, isShow: false },
        });

        this.filterHelper.setQueryParamHistory('sort', sort || '', false, {
          isShortenUrlParam,
          urlScheme: this.filterHelper.getUrlScheme(),
        });
        this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);

        // Emit SORT_CHANGED event to trigger UI updates
        this.filterHelper.emit(EVENT_NAMES.SORT_CHANGED);
      }

      const stickyStatus = this.filterStickyController.getStickyStatus();
      if (stickyStatus.stickyEnabled) {
        this.domHelper.scrollToTop({ behavior: 'auto' });
      }
      return;
    }

    // Toggle dropdown on button/value click
    if (isValue || isButton) {
      const { isShow, sort } = sortState;
      this.filterStore.setState({
        sort: { sort, isShow: !isShow },
      });

      // Emit SORT_CHANGED event to update UI
      this.filterHelper.emit(EVENT_NAMES.SORT_CHANGED, { sort });
    }
  }

  /**
   * Updates the sort dropdown UI based on current state
   * This should be called when SORT_CHANGED event is emitted
   */
  updateSortUI(): void {
    const document = this.filterStore.getState('document') as HTMLElement;
    const sortState = this.filterStore.getState('sort') as { isShow: boolean; sort: string | null };

    if (!sortState) return;

    const childElementsList = document.querySelector('.boost-sd__sorting-list');
    const childElementsButton = document.querySelector('.boost-sd__sorting-button');

    if (!childElementsList || !childElementsButton) return;

    const isActive = childElementsList.classList.contains('boost-sd__sorting-list--active');

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const currentSort = this.filterStore.getState('sort') as {
          isShow: boolean;
          sort: string | null;
        };
        if (currentSort?.isShow) {
          this.filterStore.setState({
            sort: { sort: currentSort.sort, isShow: false },
          });
          childElementsList.classList.remove('boost-sd__sorting-list--active');
          childElementsButton.classList.remove('boost-sd__sorting-button--active');
          childElementsButton.setAttribute('aria-expanded', 'false');
          (childElementsButton as HTMLElement).focus();
        }
        document.removeEventListener('keydown', handleEscapeKey);
      }
    };

    if (sortState?.isShow && !isActive) {
      childElementsList.classList.add('boost-sd__sorting-list--active');
      childElementsButton.classList.add('boost-sd__sorting-button--active');
      childElementsButton.setAttribute('aria-expanded', 'true');

      // Attach Escape key listener
      document.addEventListener('keydown', handleEscapeKey);
    } else if (!sortState?.isShow && isActive) {
      childElementsList.classList.remove('boost-sd__sorting-list--active');
      childElementsButton.classList.remove('boost-sd__sorting-button--active');
      childElementsButton.setAttribute('aria-expanded', 'false');

      // Remove Escape key listener
      document.removeEventListener('keydown', handleEscapeKey);
    }

    // Attach click handlers to each option
    childElementsList.querySelectorAll('.boost-sd__sorting-option').forEach((option) => {
      const handleClick = (e: Event) => {
        this.handleSort(option as HTMLElement, e);
      };

      // Remove existing listener if any to avoid duplicates
      option.removeEventListener('click', handleClick);
      option.addEventListener('click', handleClick);
    });
  }

  /**
   * Handles clicks outside the sort dropdown to close it
   *
   * @param target - The clicked element
   */
  handleClickOutsideSort(target: HTMLElement): void {
    const document = this.filterStore.getState('document') as HTMLElement;
    const sortingList = document.querySelector('.boost-sd__sorting-list');

    if (sortingList) {
      if (!target?.closest('.boost-sd__sorting-button')) {
        const sortState = this.filterStore.getState('sort') as {
          isShow: boolean;
          sort: string | null;
        };

        if (sortState?.isShow) {
          this.filterStore.setState({
            sort: { ...sortState, isShow: false },
          });

          // Emit event to update UI
          this.filterHelper.emit(EVENT_NAMES.SORT_CHANGED);
        }
      }
    }
  }

  /**
   * Handles clicks outside the quick add to cart mini popup to close it.
   *
   * Closes any open quick cart selection popup when clicking outside of it,
   * except when clicking the select option button itself. This provides a better
   * user experience by allowing easy dismissal of the popup. Override this method
   * to customize popup closing behavior or add additional close triggers.
   *
   * @param target - The clicked DOM element
   *
   * @example
   * Extend to add animation before closing:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleClickOutsideMiniPopup(target) {
   *       const miniPopupOpening = document.querySelector(
   *         '.boost-sd__popup-select-option[style="display: block;"]'
   *       );
   *
   *       if (miniPopupOpening && !target.closest('.boost-sd__button--select-option')) {
   *         // Add fade-out animation before closing
   *         miniPopupOpening.classList.add('fade-out');
   *         setTimeout(() => {
   *           super.handleClickOutsideMiniPopup(target);
   *         }, 200);
   *         return;
   *       }
   *
   *       super.handleClickOutsideMiniPopup(target);
   *     }
   *   };
   * });
   * ```
   */
  handleClickOutsideMiniPopup(target: HTMLElement): void {
    // Close quick cart popup selection when click out side
    if (target.closest('.boost-sd__button--select-option')) {
      return;
    }
    const miniPopup = target
      ?.closest('.boost-sd__product-item')
      ?.querySelector('.boost-sd__popup-select-option');
    const miniPopupOpening = document.querySelector(
      '.boost-sd__popup-select-option[style="display: block;"]'
    ) as HTMLElement | null;
    if (!miniPopup && miniPopupOpening) {
      miniPopupOpening.style.display = 'none';
    }
  }

  /**
   * Handles pagination click events
   * Determines the new page based on the clicked element and triggers pagination action
   *
   * @param target - The clicked pagination element
   */
  handlePagination(target: HTMLElement) {
    const paginationState = (this.filterStore.getState('pagination') as { page: number }) || {
      page: 1,
    };
    let behavior: 'refresh' | 'more' | 'previous' = 'refresh';
    let newPage = paginationState.page || 1;

    // Determine new page based on clicked element
    if (target.classList.contains('boost-sd__pagination-number')) {
      // Page link
      const pageValue = target.textContent?.trim() || '1';
      newPage = this.calcPageAction(newPage, pageValue);
    } else if (target.classList.contains('boost-sd__pagination-button--prev')) {
      // Previous button
      newPage = this.calcPageAction(newPage, 'prev');
    } else if (target.classList.contains('boost-sd__pagination-button--next')) {
      // Next button
      newPage = this.calcPageAction(newPage, 'next');
    } else if (
      target.closest('.boost-sd__pagination-button--load-previous') ||
      target.closest('.boost-sd__pagination-infinite-scroll-container-button')
    ) {
      // Load previous - use current page state, not session storage
      newPage = this.calcPageAction(newPage, 'prev');
      behavior = 'previous';
    } else if (target.closest('.boost-sd__pagination-button--load-more')) {
      // Load more - use current page state, not session storage
      newPage = this.calcPageAction(newPage, 'next');
      behavior = 'more';
    }
    return { newPage, behavior };

    // await this.processPaginatingAction(newPage, behavior);
  }

  /**
   * Calculates the new page number based on action.
   *
   * Determines the target page number from pagination actions like 'prev', 'next',
   * or direct page number strings. Ensures page numbers don't go below 1.
   * Override this method to add custom page calculation logic, such as skipping
   * pages or implementing custom pagination behaviors.
   *
   * @param currentPage - Current page number (1-based)
   * @param action - Action string: 'prev' for previous, 'next' for next, or numeric string for direct page
   * @returns New page number (minimum 1)
   *
   * @example
   * Extend to implement page skipping:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     protected calcPageAction(currentPage, action) {
   *       switch (action) {
   *         case 'prev':
   *           // Jump back 2 pages instead of 1
   *           return Math.max(1, currentPage - 2);
   *         case 'next':
   *           // Jump forward 2 pages instead of 1
   *           return currentPage + 2;
   *         default:
   *           return super.calcPageAction(currentPage, action);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected calcPageAction(currentPage: number, action: string): number {
    switch (action) {
      case 'prev':
        return Math.max(1, currentPage - 1);
      case 'next':
        return currentPage + 1;
      default: {
        // Direct page number
        const pageNum = parseInt(action, 10);
        return isNaN(pageNum) ? currentPage : pageNum;
      }
    }
  }

  /**
   * Handles toggling the limit list dropdown
   * Manages show/hide state and triggers UI update
   *
   * @param target - The clicked element
   */
  handleToggleShowLimitList(target: HTMLElement): void {
    const limitListState = (this.filterStore.getState('limitList') as { isShow: boolean }) || {
      isShow: false,
    };

    const { isShow } = limitListState;

    // Toggle the isShow state
    this.filterStore.setState({
      limitList: {
        isShow: !isShow,
      },
    });

    // Emit event to update UI
    this.filterHelper.emit(EVENT_NAMES.LIMIT_LIST_CHANGED);
  }

  /**
   * Updates the limit list dropdown UI based on current state
   * This should be called when LIMIT_LIST_CHANGED event is emitted
   */
  updateLimitListUI(): void {
    const document = this.filterStore.getState('document') as HTMLElement;
    const limitListState = this.filterStore.getState('limitList') as { isShow: boolean };

    if (!limitListState) return;

    const limitBtn = document.querySelector('.boost-sd__show-limit-list-button');
    const limitListUl = document.querySelector('.boost-sd__show-limit-list-list');

    if (!limitBtn || !limitListUl) return;

    if (limitListState.isShow) {
      limitBtn.classList.add('boost-sd__show-limit-list-button--active');
      limitListUl.classList.add('boost-sd__show-limit-list-list--active');
    } else {
      limitBtn.classList.remove('boost-sd__show-limit-list-button--active');
      limitListUl.classList.remove('boost-sd__show-limit-list-list--active');
    }
  }

  /**
   * Handles clicking on a limit list option
   * Updates the limit, saves to local storage, and resets filter to apply new limit
   *
   * @param target - The clicked option element
   */
  handleLimitListOptionClick(target: HTMLElement): void {
    const limit = target.innerHTML;
    const limitNum = Number(limit);

    // Add active class to selected option
    target.classList.add('boost-sd__show-limit-list-option--active');

    // Update defaultParams limit
    const defaultParams = this.filterStore.getState('defaultParams') as { limit?: number };
    if (defaultParams) {
      defaultParams.limit = limitNum;
    }

    // Save to local storage
    this.filterHelper.setLocalStorage(LIMIT, limitNum.toString());

    // Scroll to top
    this.domHelper.scrollToTop({ behavior: 'auto' });

    // Reset filter with new limit
    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);

    // Toggle the limit list closed
    this.filterHelper.emit(EVENT_NAMES.LIMIT_LIST_CHANGED);
  }

  /**
   * Handles clicks outside the limit list dropdown to close it
   *
   * @param target - The clicked element
   */
  handleClickOutsideLimitList(target: HTMLElement): void {
    const document = this.filterStore.getState('document') as HTMLElement;
    const limitList = document.querySelector('.boost-sd__show-limit-list');

    if (limitList) {
      if (!target?.classList?.contains('boost-sd__show-limit-list-button')) {
        const limitListState = this.filterStore.getState('limitList') as { isShow: boolean };

        if (limitListState?.isShow) {
          this.filterStore.setState({
            limitList: { isShow: false },
          });

          // Emit event to update UI
          this.filterHelper.emit(EVENT_NAMES.LIMIT_LIST_CHANGED);
        }
      }
    }
  }

  /**
   * Handles clicking on a view-as icon (grid/list view toggle)
   * Updates the viewAs setting and triggers filter refresh
   *
   * @param target - The clicked element
   */
  handleViewAsClick(target: HTMLElement): void {
    const defaultParams = this.filterStore.getState('defaultParams') as {
      viewAs?: string;
      device?: string;
    };

    if (!defaultParams) return;

    // Get the viewAs value from the clicked element
    if (target.classList.contains('boost-sd__view-as-icon')) {
      defaultParams.viewAs = target.getAttribute('data-value') || '';
    } else {
      const viewAsIcon = target.closest('.boost-sd__view-as-icon');
      defaultParams.viewAs = viewAsIcon?.getAttribute('data-value') || '';
    }

    // Determine device type based on screen size
    const breakpointMobile =
      (this.appService.TAEAppConfig.generalSettings as { breakpointmobile?: number })
        ?.breakpointmobile || 575;
    const isMobileSize = this.filterHelper.isMobile(breakpointMobile);
    defaultParams.device = isMobileSize ? this.filterHelper.detectDeviceByWidth() : '';

    // Update active view button state
    this.filterStore.setState({
      activeViewAsButton: defaultParams.viewAs,
    });
    this.filterStore.setState({ defaultParams });

    // Scroll to top if sticky is enabled
    const stickyStatus = this.filterStickyController.getStickyStatus();
    if (stickyStatus.stickyEnabled) {
      this.domHelper.scrollToTop({ behavior: 'auto' });
    }

    // Trigger filter reset to apply view change
    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);
  }

  /**
   * Handles closing the quick add to cart popup.
   *
   * Hides the quick add to cart option selection popup and returns focus to the
   * select option button for accessibility. Override this method to customize
   * close behavior or add cleanup logic when the popup closes.
   *
   * @param target - The clicked close button element
   *
   * @example
   * Extend to track popup closure for analytics:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleCloseQuickAddToCart = (target) => {
   *       // Track popup closure
   *       const productItem = target.closest('.boost-sd__product-item');
   *       const productId = productItem?.getAttribute('data-product-id');
   *       analytics.track('quick_add_closed', { product_id: productId });
   *
   *       // Call parent implementation
   *       const popupSelection = target.closest('.boost-sd__popup-select-option');
   *       popupSelection.style.display = 'none';
   *       target.closest('.boost-sd__product-item')
   *         .querySelector('.boost-sd__button--select-option')
   *         .focus();
   *     };
   *   };
   * });
   * ```
   */
  handleCloseQuickAddToCart = (target) => {
    const popupSelection = target.closest('.boost-sd__popup-select-option');
    popupSelection.style.display = 'none';
    target
      .closest('.boost-sd__product-item')
      .querySelector('.boost-sd__button--select-option')
      .focus();
  };

  /**
   * Handles metadata actions from data-metadata attributes.
   *
   * Routes legacy metadata actions from DOM elements to appropriate event handlers.
   * This supports the legacy filter tree system where elements have data-metadata
   * attributes containing action configuration objects. Each action type triggers
   * a corresponding EVENT_NAMES event. Override this method to add custom action
   * types or modify action routing logic.
   *
   * @param actions - Object where keys are action types and values are action configuration objects
   * @param target - The clicked DOM element that triggered the actions
   *
   * @remarks
   * Supported legacy action types:
   * - optionList: Filter option list interactions
   * - clearAllFilter: Clear all applied filters
   * - clearFilter: Clear specific filter
   * - removeFilterFromRefineBy: Remove from refine-by section
   * - addToCart: Add product to cart
   * - quickView: Open quick view modal
   * - optionCollection: Collection multi-level navigation
   * - filterOptionCollapse: Collapse/expand filter options
   * - quickAddToCart: Quick add to cart popup
   * - applyFilterOption: Apply filter option selection
   * - resetFilterOption: Reset filter option to default
   * - expandCollapseMultiLevel: Multi-level filter expansion
   *
   * @example
   * Extend to add custom action type:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     protected handleMetadataActions(actions, target) {
   *       // Handle custom action before processing standard actions
   *       if (actions.customShopAction) {
   *         this.handleCustomShopAction(actions.customShopAction, target);
   *       }
   *
   *       // Process standard actions
   *       super.handleMetadataActions(actions, target);
   *     }
   *   };
   * });
   * ```
   */
  protected handleMetadataActions(actions: Record<string, any>, target: HTMLElement): void {
    Object.keys(actions).forEach((key) => {
      const action = actions[key];
      this.handleAction(key, action, target);
    });
  }

  /**
   * Routes individual action types to their corresponding event handlers.
   *
   * Central action dispatcher that emits appropriate EVENT_NAMES events based on
   * the action type. This is called by handleMetadataActions and handleActionMapping
   * to process both legacy metadata actions and action mapping system actions.
   * Override this method to intercept specific action types or add custom action
   * handling logic.
   *
   * @param key - The action type string (e.g., 'optionList', 'clearAllFilter')
   * @param action - The action configuration object containing action data
   * @param target - The DOM element that triggered the action
   *
   * @remarks
   * Each action type emits a corresponding EVENT_NAMES event with the action
   * configuration and target element. Unknown action types are logged as warnings
   * to help identify deprecated or misconfigured actions.
   *
   * @example
   * Extend to add validation for specific actions:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleAction(key, action, target) {
   *       // Add validation before clearing filters
   *       if (key === 'clearAllFilter') {
   *         if (!confirm('Clear all filters?')) {
   *           return; // Cancel action
   *         }
   *       }
   *
   *       // Add custom action type
   *       if (key === 'customExport') {
   *         this.filterHelper.emit('EXPORT_FILTERS', { action, target });
   *         return;
   *       }
   *
   *       super.handleAction(key, action, target);
   *     }
   *   };
   * });
   * ```
   */
  handleAction(key: string, action: any, target: HTMLElement): void {
    switch (key) {
      case 'optionList':
        this.filterHelper.emit(EVENT_NAMES.OPTION_LIST, { action, target });
        break;
      case 'clearAllFilter':
        this.filterHelper.emit(EVENT_NAMES.CLEAR_ALL_FILTER, { action, target });
        break;
      case 'clearFilter':
        this.filterHelper.emit(EVENT_NAMES.CLEAR_FILTER, { action, target });
        break;
      case 'removeFilterFromRefineBy':
        this.filterHelper.emit(EVENT_NAMES.REMOVE_FILTER_FROM_REFINE_BY, { action, target });
        break;
      case 'addToCart':
        this.filterHelper.emit(EVENT_NAMES.ADD_TO_CART, { action, target });
        break;
      case 'quickView':
        this.filterHelper.emit(EVENT_NAMES.QUICK_VIEW, { action, target });
        break;
      case 'optionCollection':
        this.filterHelper.emit(EVENT_NAMES.OPTION_COLLECTION, { action, target });
        break;
      case 'filterOptionCollapse':
        this.filterHelper.emit(EVENT_NAMES.FILTER_OPTION_COLLAPSE, { action, target });
        break;
      case 'quickAddToCart':
        this.filterHelper.emit(EVENT_NAMES.QUICK_ADD_TO_CART, { action, target });
        break;
      case 'applyFilterOption':
        this.filterHelper.emit(EVENT_NAMES.APPLY_FILTER_OPTION, { action, target });
        break;
      case 'resetFilterOption':
        this.filterHelper.emit(EVENT_NAMES.RESET_FILTER_OPTION, { action, target });
        break;
      case 'expandCollapseMultiLevel':
        this.filterHelper.emit(EVENT_NAMES.EXPAND_COLLAPSE_MULTI_LEVEL, { action, target });
        break;
      case 'goToProductDetail':
        this.filterHelper.emit(EVENT_NAMES.GO_TO_PRODUCT_DETAIL, { action, target });
        break;
      default:
        // Unknown action type - log for debugging
        console.warn(`Unknown metadata action: ${key}`, action);
    }
  }

  /**
   * Handles action mapping system for legacy filter tree actions.
   *
   * Maps data-action attributes to their corresponding handler functions stored
   * in the actionMapping state. This legacy system uses attributes in the format
   * "key.id" to look up action handlers. Override this method to customize action
   * mapping lookup logic or add preprocessing for mapped actions.
   *
   * @param target - The clicked DOM element with data-action attribute
   *
   * @remarks
   * Action attribute format: "key.id" where:
   * - key: Action category (e.g., 'option', 'filter')
   * - id: Unique identifier for the specific action
   *
   * The actionMapping state structure:
   * ```typescript
   * {
   *   [key]: {
   *     [id]: {
   *       [actionHandler]: actionConfig
   *     }
   *   }
   * }
   * ```
   *
   * This is a legacy system being phased out in favor of EVENT_NAMES.
   *
   * @example
   * Extend to add logging for mapped actions:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     protected handleActionMapping(target) {
   *       const actionId = target.dataset.action || target.getAttribute('data-action');
   *
   *       if (actionId) {
   *         // Log action for debugging
   *         console.log('Action mapping triggered:', actionId);
   *
   *         // Track action for analytics
   *         analytics.track('filter_action', { action_id: actionId });
   *       }
   *
   *       super.handleActionMapping(target);
   *     }
   *   };
   * });
   * ```
   */
  protected handleActionMapping(target: HTMLElement): void {
    const actionId = target.dataset.action || target.getAttribute('data-action');
    if (!actionId) return;

    const actionMapping = this.filterStore.getState('actionMapping');
    if (!actionMapping) return;

    const [key, id] = actionId.split('.');

    if (actionMapping[key] && actionMapping[key][id]) {
      const actionHandler = Object.keys(actionMapping[key][id])[0];
      const action = actionMapping[key][id][actionHandler];
      this.handleAction(actionHandler, action, target);
    }
  }

  /**
   * Main click event handler that routes all filter widget click interactions.
   *
   * Central event dispatcher for all click events within the filter widget.
   * Processes metadata actions, action mappings, and routes clicks to specialized
   * handlers based on element classes and context. This is the entry point for
   * all click interactions in the filter system. Override this method to add
   * global click preprocessing, custom routing logic, or shop-specific behaviors.
   *
   * @param event - The native click event from the DOM
   *
   * @remarks
   * Click handling flow:
   * 1. Stores focus state for accessibility
   * 2. Handles recent search suggestions
   * 3. Routes product item clicks
   * 4. Prevents default for internal anchor links
   * 5. Processes metadata actions (legacy system)
   * 6. Processes action mappings (legacy system)
   * 7. Routes to specialized handlers (sort, pagination, limit, view-as, etc.)
   * 8. Handles filter tree toggle and mobile interactions
   * 9. Handles bundle and scroll-to-top interactions
   *
   * Most interactions emit EVENT_NAMES events for decoupled handling.
   *
   * @example
   * Extend to add global click tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterHandler', (FilterHandler) => {
   *   return class CustomFilterHandler extends FilterHandler {
   *     handleCoreClick(event) {
   *       const target = event.target;
   *
   *       // Track all clicks for analytics
   *       analytics.track('filter_click', {
   *         element: target.className,
   *         page: window.location.pathname
   *       });
   *
   *       // Add custom click prevention logic
   *       if (target.closest('.custom-disabled-area')) {
   *         event.preventDefault();
   *         event.stopPropagation();
   *         return;
   *       }
   *
   *       super.handleCoreClick(event);
   *     }
   *   };
   * });
   * ```
   */
  handleCoreClick(event: Event): void {
    event.preventDefault();
    const target = event.target as HTMLElement;

    // Resolve metadata from either modern data-metadata or legacy metadata attribute.
    const elementWithMetadata =
      (target.closest('[data-metadata]') as HTMLElement | null) ||
      (target.closest('[metadata]') as HTMLElement | null);

    const metadataValue =
      elementWithMetadata?.dataset.metadata ||
      elementWithMetadata?.getAttribute('metadata') ||
      '{}';
    const metadataObject = this.filterHelper.safeParseJSON(metadataValue) as
      | {
          action?: Record<string, unknown>;
        }
      | null
      | undefined;
    const metadataAction = metadataObject?.action;

    const currentButton = target.closest('button');
    if (currentButton && currentButton.id) {
      this.filterStore.setState({ filterCurrentFocus: currentButton.id });
    }

    handleRecentSearchForSuggestionTerm(event as MouseEvent);
    if (
      target.closest('.boost-sd__product-item, .boost-sd__product-item-list-view-layout') &&
      !target.closest('.boost-sd__popup-select-option') &&
      (!target.closest('.boost-sd__product-swatch') ||
        target.closest('.boost-sd__product-swatch-more')) &&
      !target.closest('.boost-sd__bulk-quantity') &&
      !metadataAction
    ) {
      this.filterHelper.emit(EVENT_NAMES.GO_TO_PRODUCT_DETAIL, { target, originalEvent: event });
    }

    // action.optionCollection -> collection multi level - prevent default
    // Not prevent when click product banner - !target?.closest('.boost-sd__product-banner-link')
    const isAnchor = target.tagName === 'A';
    const isWidgetInternalLink = !target.closest('.boost-sd__product-banner-link');

    if (
      isAnchor &&
      (!target.getAttribute('href') || metadataAction?.optionCollection) &&
      isWidgetInternalLink
    ) {
      event.preventDefault();
    }

    // Handle metadata actions - legacy filter tree interactions
    if (metadataAction) {
      this.handleMetadataActions(metadataAction, target);
    }

    // Handle data-action attribute - legacy action mapping system
    this.handleActionMapping(target);

    if (
      target.classList &&
      [...target.classList].some((className) => className.startsWith('boost-sd__sorting-'))
    ) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_SORT, { target, originalEvent: event });
      return;
    }
    if (
      target.classList.contains('boost-sd__pagination-number') ||
      target.classList.contains('boost-sd__pagination-button')
    ) {
      this.filterHelper.emit(EVENT_NAMES.PAGINATION, { target, originalEvent: event });
      return;
    }
    if (target.classList.contains('boost-sd__show-limit-list-button')) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_TOGGLE_SHOW_LIMIT_LIST, { target });
      return;
    }
    if (target.classList.contains('boost-sd__show-limit-list-option')) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_LIMIT_LIST_OPTION_CLICK, { target });
      return;
    }
    if (
      target.classList.contains('boost-sd__view-as-icon') ||
      target.closest('.boost-sd__view-as-icon')
    ) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_VIEW_AS_CLICK, { target });
      return;
    }

    // click button show result, close , overlay in style off-canvas -> close
    const { filterLayout, filterTreeVerticalStyle } = this.appService.filterSettings;

    const filterTreeVerticalStyleOffCanvas =
      filterLayout === 'vertical' && filterTreeVerticalStyle === 'style-off-canvas';
    // click button show result, close , overlay in style off-canvas -> close
    if (
      filterTreeVerticalStyleOffCanvas &&
      !this.filterHelper.isMobile() &&
      (target.closest('.boost-sd__button.boost-sd__button--result') ||
        target.closest('.boost-sd__filter-tree-vertical-close-filter--left') ||
        target.classList.contains('boost-sd__filter-tree-vertical-sticky-overlay'))
    ) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_SHOW_RESULT, { target });
      return;
    }

    if (target.closest(FILTER_TREE_TOGGLE_BUTTON_SELECTOR)) {
      // click mobile
      if (target.closest(FILTER_MOBILE_ICON_SELECTOR)) {
        this.filterHelper.emit(EVENT_NAMES.SHOW_MOBILE_FILTER_TREE, { target });
        return;
      }

      // click desktop
      this.filterHelper.emit(EVENT_NAMES.TOGGLE_FILTER_TREE_BUTTON_DESKTOP, { target });
      return;
    }

    if (
      target.closest('.boost-sd__filter-tree-vertical-close-filter') ||
      target.closest('.boost-sd__button--close') ||
      target.classList.contains('boost-sd__filter-tree-vertical-sticky-overlay') ||
      (target.closest('.boost-sd__filter-tree-vertical-show-result') &&
        this.filterHelper.isMobile())
    ) {
      this.filterHelper.emit(EVENT_NAMES.CLOSE_MOBILE_FILTER_TREE, { target });
      return;
    }

    if (target.closest('.boost-sd__button--back')) {
      this.filterHelper.emit(EVENT_NAMES.BACK_FILTER_TREE_MOBILE, { target });
      return;
    }

    if (target.closest('.boost-sd__popup-select-option-close-btn')) {
      this.filterHelper.emit(EVENT_NAMES.CLOSE_QUICK_ADD_TO_CART, { target });
      return;
    }

    if (target.closest('.boost-sd__scroll-to-top')) {
      this.filterHelper.emit(EVENT_NAMES.SCROLL_TO_TOP, { target });
      return;
    }

    if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_CLICK_EMBEDDED_BUNDLE, { target });
      return;
    }
    if (target?.closest('.boost-sd__product-item--volume-bundle')) {
      this.filterHelper.emit(EVENT_NAMES.HANDLE_CLICK_VOLUME_BUNDLE, { target });
      return;
    }
  }
}
